/**
 * <fly-to-cart> — premium add-to-cart animation.
 * A small element flies along an accelerated curve from a source to the cart
 * icon, then removes itself. Self-contained (no theme imports).
 * Adapted from Horizon 3.5.1.
 *
 * Usage: set EITHER live elements (`.source` + `.destination`) OR pre-captured
 * rects (`.sourceRect` + `.destinationRect`). Rects are preferred when the
 * source may be removed from the DOM by a section morph (cart upsell/FBT).
 */
class FlyToCart extends HTMLElement {
  /** @type {Element | undefined} */
  source;
  /** @type {Element | undefined} */
  destination;
  /** @type {DOMRect | undefined} */
  sourceRect;
  /** @type {DOMRect | undefined} */
  destinationRect;
  /** @type {boolean} */
  useSourceSize = false;

  connectedCallback() {
    // Preferred path: fixed rects (survives DOM removal of the source).
    if (this.sourceRect && this.destinationRect) {
      this.#animate(this.sourceRect, this.destinationRect);
      return;
    }

    if (!this.source || !this.destination) {
      this.remove();
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      /** @type {DOMRectReadOnly | null} */
      let sourceRect = null;
      /** @type {DOMRectReadOnly | null} */
      let destinationRect = null;
      entries.forEach((entry) => {
        if (entry.target === this.source) sourceRect = entry.boundingClientRect;
        else if (entry.target === this.destination) destinationRect = entry.boundingClientRect;
      });
      if (sourceRect && destinationRect) this.#animate(sourceRect, destinationRect);
      observer.disconnect();
    });
    observer.observe(this.source);
    observer.observe(this.destination);
  }

  #yieldToMain() {
    // scheduler.yield() when available, else a rAF + macrotask fallback.
    if (typeof scheduler !== 'undefined' && 'yield' in scheduler) {
      // @ts-ignore
      return scheduler.yield();
    }
    return new Promise((resolve) => requestAnimationFrame(() => setTimeout(resolve, 0)));
  }

  /**
   * @param {DOMRectReadOnly} sourceRect
   * @param {DOMRectReadOnly} destinationRect
   */
  #animate = async (sourceRect, destinationRect) => {
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2,
      y: sourceRect.top + sourceRect.height / 2,
    };
    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2,
      y: destinationRect.top + destinationRect.height / 2,
    };

    if (this.useSourceSize) {
      this.style.setProperty('--width', `${sourceRect.width}px`);
      this.style.setProperty('--height', `${sourceRect.height}px`);
    }
    this.style.setProperty('--start-x', `${startPoint.x}px`);
    this.style.setProperty('--start-y', `${startPoint.y}px`);
    this.style.setProperty('--travel-x', `${endPoint.x - startPoint.x}px`);
    this.style.setProperty('--travel-y', `${endPoint.y - startPoint.y}px`);

    await this.#yieldToMain();

    const animations = this.getAnimations();
    if (!animations.length) {
      this.remove();
      return;
    }
    await Promise.allSettled(animations.map((a) => a.finished));
    this.remove();
  };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
